import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Modal,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_RED, GRAPH_EXPORT_LIMIT_MULTIPLIER, MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY } from 'src/utils/const';
import { useGetCreditsRemaining } from 'src/hooks/useCustomHooks';
import { COLOR_GREY_200 } from 'src/utils/colors';
import { getGraphExportCreditRequired, makeDownloadableBlob } from 'src/utils/functions';
import { updateUserExplorerPageRowDataExportUsage } from '../../ExplorerPage/helper';
import { useDispatch } from 'react-redux';
import { useCanAccessScalePlan } from 'src/hooks/useFeatureAccess';

const BoxedDiv = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box sx={{ backgroundColor: COLOR_GREY_200, padding: '10px', borderRadius: '5px', mb: '10px' }}>
      {children}
    </Box>
  );
};

const ValueSpan = ({ children }: { children: React.ReactNode }) => {
  return <span style={{ fontSize: '1.1em', color: '#000' }}>{children}</span>;
};

const GraphExportModal = ({
  open,
  handleClose,
  selectedGraphExportDetails,
  setSelectedGraphExportDetails,
  handleOnSampleClick,
  isCacheLoading
}: {
  open: boolean;
  handleClose: () => void;
  selectedGraphExportDetails: any;
  setSelectedGraphExportDetails: any;
  handleOnSampleClick: (planName: string) => void;
  isCacheLoading: boolean;
}) => {
  const [error, setError] = useState<string | null>(null);

  const { creditsRemaining, creditsUsed, totalCredits } = useGetCreditsRemaining(true);
  const dispatch = useDispatch();

  const isScaleUser = useCanAccessScalePlan();


  if (!selectedGraphExportDetails || isCacheLoading) {
    handleClose();

    return <></>;
  }  

  selectedGraphExportDetails.graphData = selectedGraphExportDetails?.graphData && selectedGraphExportDetails.graphData.filter((item: any) => item[1] !== null && item[1] !== undefined && item[1] !== '');    
  const creditRequiredToExport = getGraphExportCreditRequired(selectedGraphExportDetails.graphData.length, selectedGraphExportDetails.graphType);
  const creditsUsuagePerRow = GRAPH_EXPORT_LIMIT_MULTIPLIER[selectedGraphExportDetails.graphType];

  const creditUsuagePerRowText = `This export will spend ${creditsUsuagePerRow} credits per row exported. Total rows to export: ${creditRequiredToExport}.`;

  const handleExport = () => {
    if (creditsRemaining < creditRequiredToExport) {
      return;
    }

    if (!isScaleUser) {
      handleOnSampleClick('scale');
      handleClose();
      return;
    }

    try {
      makeDownloadableBlob(
        selectedGraphExportDetails.graphData,
        selectedGraphExportDetails.fieldName,
        false,
        false
      );

      updateUserExplorerPageRowDataExportUsage(creditRequiredToExport, dispatch);

      setSelectedGraphExportDetails(null);
    } catch (error) {
      setError('Error exporting graph: ' + error);
      console.error('Error exporting graph: ', error);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'clamp(300px, 100%, 500px)',
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: 24,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <IconButton onClick={handleClose} sx={{ position: 'absolute', top: '5px', right: '5px' }}>
          <CloseIcon sx={{ color: COLOR_RED }} />
        </IconButton>

        <Typography variant="h6" sx={{ fontSize: '22px', mb: '20px' }}>
          Export Graph
        </Typography>

        {/* Credits remaining */}
        <BoxedDiv>
          <Typography variant="body1" sx={{ fontSize: '16px', mb: '10px' }}>
            <strong>Credits Remaining:</strong>{' '}
            <ValueSpan>
              {isScaleUser ? (
                <>
                  {creditsRemaining}/{totalCredits}
                </>
              ) : (
                0
              )}
            </ValueSpan>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={
              isScaleUser
                ? (creditsRemaining / (Math.max(totalCredits as number, 1) || 1)) * 100
                : 0
            }
          />
        </BoxedDiv>

        {/* credit required to export */}
        <BoxedDiv>
          <Typography variant="body1" sx={{ fontSize: '16px', mb: '10px' }}>
            <strong>Credits Required to Export:</strong>{' '}
            <ValueSpan>{creditRequiredToExport}</ValueSpan>
            <br />
            <Typography variant="body2">
              * {creditUsuagePerRowText}
            </Typography>
          </Typography>
        </BoxedDiv>

        {/* Alert if no credits left */}
        {creditsRemaining < creditRequiredToExport && (
          <Alert severity="error" sx={{ mb: '10px' }}>
            You have no credits left to export this graph data, please wait for your limit to reset
            next month.
          </Alert>
        )}

        {!isScaleUser && (
          <Alert severity="warning" sx={{ mb: '10px' }}>
            To export this graph data, please upgrade to the{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                handleOnSampleClick('scale');
                handleClose();
              }}
            >
              {' '}
              Scale Plan{' '}
            </Link>
            . With the Scale Plan, you'll get up to 1,000 rows of exportable data every month.
          </Alert>
        )}

        {/* Button to export */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleExport}
          disabled={creditsRemaining < creditRequiredToExport}
        >
          Export {!isScaleUser ? '(Upgrade to Scale Plan)' : ''}
        </Button>

        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </Modal>
  );
};

export default GraphExportModal;
